import { FC, useEffect, useState } from 'react'
import { X, ChevronRight } from 'react-feather'
import clsx from 'clsx'

import { useAssignment, useUtils, useShowMessage } from '@pepper/ui'
import { getTagSetFromGroupedKey } from '@pepper/utils/src/function/getTagsetFromGroupKey'
import { getCreateProjectJSON } from '@pepper/utils/src/api/utils/utils'
import getFormFields from '@pepper/utils/src/function/getFormFields'
import transformFields from '@pepper/utils/src/function/transformFields'

import AssignmentBrief from 'container/AssignmentDetails/TabContent/AssignmentBrief/AssignmentBrief'
import { ReactComponent as IconAssignment } from 'assets/images/apps-assignment-brief.svg'

import styles from './_.module.css'

interface Props {
  assignmentDataExpanded: boolean
  setAssignmentDataExpanded?: (val: AppPane) => void
  assignmentId: string
}
export interface FieldKeys {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value:
    | string
    | string[]
    | Attachment[]
    | undefined
    | { richText: string; attachments: { tag: string }[]; loomUrls: string[] }
  title: string
  type: string
  id?: string
  options?: { label: string; value: number | string }[] | BlockSelectOption[]
}

const AssignmentBriefEditor: FC<Props> = ({
  assignmentDataExpanded,
  setAssignmentDataExpanded,
  assignmentId,
}) => {
  const [assignmentBrief, setAssignmentBrief] = useState<FieldKeys[]>([])

  const showMessage = useShowMessage()

  const { data: assignmentDetails } = useAssignment({ assignmentId })
  const { verticals, project_categories } = useUtils()

  const projectCategory = assignmentDetails?.category

  const verticalId = assignmentDetails?.vertical.id

  useEffect(() => {
    const fetchFieldsBaseOnVertical = async (verticalId: number) => {
      const response = await getCreateProjectJSON(
        verticalId,
        assignmentDetails?.json_version
      )

      if (response.status === 'error') {
        throw new Error('Failed to fetch JSON file')
      }

      const keySetContainingTagField = getTagSetFromGroupedKey({
        verticalResponse: response?.data,
      })
      const otherLinks =
        assignmentDetails?.other_links?.map((it: string) => {
          const splitItem = it?.split(',')
          return {
            tag: splitItem?.[0]?.split('tag:')?.[1] ?? '',
            link: splitItem?.[1]?.split('link:')?.[1] ?? '',
          }
        }) ?? []
      const result = getFormFields(response.data?.response)

      if (Array.isArray(result)) {
        const _fields: BackendFormField[] = transformFields(result)

        const _assignmentFields: FieldKeys[] = []

        _fields.forEach(field => {
          if (field.id === 'references') {
            _assignmentFields.push({
              type: field.type,
              title: field.label,
              value: Array.isArray(assignmentDetails?.reference_files)
                ? assignmentDetails?.reference_files.filter(file => !file.tag)
                : [],
              id: field.id,
              options: [],
            })
            return
          }
          let value = assignmentDetails?.[field.id] ?? ''
          if (
            field.type === 'html' &&
            (typeof assignmentDetails?.[field.id] === 'string' ||
              !assignmentDetails?.[field.id]) &&
            keySetContainingTagField?.[field.id]
          ) {
            const matchedTag = keySetContainingTagField?.[field.id]?.tag
            value = {
              richText: assignmentDetails?.[field.id] || '',
              attachments:
                assignmentDetails?.reference_files?.filter(
                  file => file.tag === matchedTag
                ) ?? [],
              loomUrls:
                otherLinks
                  ?.filter(
                    (otherLinkItem: { tag: string }) =>
                      otherLinkItem?.tag === matchedTag
                  )
                  ?.map((it: { link: string }) => it?.link) ?? [],
            }
          }
          _assignmentFields.push({
            type: field.type ?? '',
            title: field.label ?? '',
            value,
            id: field.id,
            options: field.options,
          })
        })

        setAssignmentBrief(_assignmentFields)
      }
    }

    if (!verticalId) return

    fetchFieldsBaseOnVertical(verticalId)
  }, [
    verticalId,
    assignmentDetails,
    showMessage,
    verticals,
    projectCategory,
    project_categories,
  ])

  if (!assignmentDataExpanded) {
    return (
      <div className={styles.wrap}>
        <div
          role="none"
          onClick={() => setAssignmentDataExpanded?.('ASSIGNMENT_BRIEF')}
          className={styles.wrapInside}
        >
          <div className={styles.fieldIconWrap}>
            <IconAssignment className={styles.fieldIcon} />
          </div>
          <div className={styles.fieldTextWrap}>
            <span className={styles.fieldTextDetails}>Assignment Brief</span>
          </div>
          <ChevronRight className={styles.expandIcon} />
        </div>
      </div>
    )
  }
  return (
    <>
      <div className={styles.header}>
        <div className={styles.fieldIconWrap}>
          <IconAssignment className={styles.fieldIcon} />
        </div>
        <span className={styles.headerText}>Assignment Brief</span>
        <X
          className={styles.closeIcon}
          onClick={() => setAssignmentDataExpanded?.('ASSIGNMENT_BRIEF')}
        />
      </div>
      <div
        className={clsx(
          styles.bodyExpWrap,
          styles.infoBodyWrap,
          styles.customScrollBar
        )}
      >
        <div className={styles.infoBodyInside}>
          {assignmentDetails && (
            <AssignmentBrief
              projectBrief={[]}
              assignmentBrief={assignmentBrief}
              data={assignmentDetails}
              isPage={false}
              loomWidth={325}
              loomHeight={325}
            />
          )}
        </div>
      </div>
    </>
  )
}
export default AssignmentBriefEditor
